import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import type { Filter, ListingsSearchEvent, InvestmentProjectIds } from './types'
import SearchService from './services/SearchService/v1'
import { getBusinessHubsFromClustersValues, getClustersByNeighborhoodsValues, getNeighborhoodsFromClustersValues } from '@/utils/locations'
import { isTruthyFilter, extractListingIdsFromProjectsSummaries } from '@/composables/useOnboarding/utils'
import { bedroomsFilter, deliveryTermFilter, neighborhoodsFilter } from '@/composables/useOnboarding/filters'
import { useLocationsStore } from '@/stores/locationsStore'

const opportunityTypeEventMap: Record<string, string> = {
  living: 'LIVING',
  investing: 'INVESTMENT'
}

const useOnboarding = () => {
  // @ts-ignore
  const { locale } = useI18n()
  // @ts-ignore
  const route = useRoute()
  // @ts-ignore
  const { $sobreplanosDataService } = useNuxtApp()

  const locationsStore = useLocationsStore()

  const getFiltersClassified = (): Filter[] => {
    const filters = [
      {
        key: 'delivery_term',
        type: 'frontend',
        value: getSelectedDeliveryTerm(),
        filter: deliveryTermFilter
      },
      {
        key: 'bedrooms_number',
        type: 'frontend',
        value: getSelectedBedroomsNumber(),
        filter: bedroomsFilter
      },
      {
        key: 'neighborhoods',
        type: 'frontend',
        value: getSelectedNeighborhoods(),
        filter: neighborhoodsFilter
      },
      {
        key: 'business_hubs',
        type: 'backend',
        value: getSelectedBusinessHubs()
      }
    ]
    return filters.filter(filter => isTruthyFilter(filter))
  }

  const getResults = async (filters: Filter[]) => {
    const searchService = new SearchService({
      $sobreplanosDataService,
      locationsQueryUtils: locationsStore.locationsQueryUtils,
      customTitle: route.query.custom_title
    })

    const searchResults = await searchService.search(filters)
    const listingIds = getVisibleIds(searchResults)

    listingsSearchEvent(filters, listingIds)

    return searchResults
  }

  const getVisibleIds = (searchResults) => {
    const visibleProjects = [
      searchResults.outstandingProject,
      ...searchResults.exactMatchProjects
    ]
    return extractListingIdsFromProjectsSummaries(visibleProjects)
  }

  const getSelectedClusters = () => {
    const selectedClustersStr = route.query.clusters
    if (typeof selectedClustersStr === 'string') {
      return selectedClustersStr.split('~')
    }

    if (route.query.neighborhoods) return getClustersByNeighborhoodsValues(route.query.neighborhoods.split('~'), locale.value).map(cluster => cluster.value)

    return []
  }

  const getSelectedNeighborhoods = () => {
    const clustersValues = getSelectedClusters()

    if (!route.query.neighborhoods) return getNeighborhoodsFromClustersValues(clustersValues, locale.value).map(neighborhood => neighborhood.value)

    return locationsStore.locationsQueryUtils.getNeighborhoodsByValues(route.query.neighborhoods.split('~')).map(neighborhood => neighborhood.value)
  }

  const getSelectedBusinessHubs = () => {
    const clustersValues = getSelectedClusters()

    if (!route.query.neighborhoods) return getBusinessHubsFromClustersValues(clustersValues, locale.value)

    return locationsStore.locationsQueryUtils.getBusinessHubsByNeighborhoodsValues(route.query.neighborhoods.split('~')).map(businessHub => businessHub.name)
  }

  const getOpportunityType = () => {
    return route.query.opportunity_type as string
  }

  const getSelectedDeliveryTerm = () => {
    const selectedDeliveryTerm = route.query.delivery_term
    if (typeof selectedDeliveryTerm === 'string') {
      return selectedDeliveryTerm.split('~')
    }
    return []
  }

  const getSelectedBedroomsNumber = () => {
    const bedroomsNumber = route.query.bedrooms_number
    if (typeof bedroomsNumber === 'string') return bedroomsNumber.split('~')
    return []
  }

  const listingsSearchEvent = (filters: Filter [], listingIds: InvestmentProjectIds[]) => {
    const breakpoints = useBreakpoints(breakpointsTailwind)
    const isMobile = breakpoints.smaller('md').value
    const bedroomsNumber = filters.find(filter => filter.key === 'bedrooms_number')?.value as number
    const deliveryTerm = filters.find(filter => filter.key === 'delivery_term')?.value as string
    const businessHubs = filters.find(filter => filter.key === 'business_hubs')?.value as string[]
    const opportunityType = getOpportunityType()
    const clusters = getSelectedClusters()

    const eventPayload: any = {
      listings: listingIds,
      screen: 'INVESTMENT_QUIZ',
      locations: [
        ...(businessHubs || [])
      ],
      device_platform: sourcePlatform(isMobile),
      user_action: 'USER_APPLIED_FILTERS'
    }

    if (deliveryTerm !== undefined) {
      eventPayload.delivery_term = deliveryTerm
    }
    if (bedroomsNumber !== undefined) {
      eventPayload.bedrooms_number = bedroomsNumber
    }
    if (opportunityType !== undefined) {
      const mappedOpportunityType = opportunityTypeEventMap[opportunityType] ?? route.query.opportunity_type?.toUpperCase()
      eventPayload.opportunity_type = mappedOpportunityType
    }

    eventPayload.locations.push(...clusters)

    snowPlowEvent(eventPayload)
  }

  const sourcePlatform = (isMobile: boolean): string => {
    return isMobile ? 'WEB_MOBILE' : 'DESKTOP'
  }

  const snowPlowEvent = (data: ListingsSearchEvent) => {
    // @ts-ignore
    const { $trackEvent } = useNuxtApp()

    $trackEvent({
      name: 'listings_searched',
      version: '3-0-0',
      data
    })
  }

  return {
    getFiltersClassified,
    getOpportunityType,
    getSelectedDeliveryTerm,
    getSelectedBedroomsNumber,
    getSelectedClusters,
    getSelectedNeighborhoods,
    getSelectedBusinessHubs,
    getResults,
    sourcePlatform,
    listingsSearchEvent
  }
}

export default useOnboarding
